import React, { useState, useEffect } from 'react';
import { saveLessonProgress } from '../firestoreFunctions';
import { auth } from '../firebase';

const Quiz = ({ data, lessonId, savedProgress, onSaveSuccess }) => {
  const [answers, setAnswers] = useState({});

  // 1. Sync local answers with the database history
  useEffect(() => {
    if (savedProgress && data) {
      const loadedAnswers = {};
      savedProgress.forEach(historyItem => {
        // Find if this specific history item belongs to one of the questions on screen
        const questionData = data.find(d => String(d.id) === String(historyItem.questionId));
        if (questionData) {
          const indices = historyItem.userAnswer.map(text => 
            questionData.options.findIndex(opt => opt.text === text)
          ).filter(idx => idx !== -1);
          
          loadedAnswers[questionData.id] = indices;
        }
      });
      setAnswers(loadedAnswers);
    }
  }, [savedProgress, data]);

const handleOptionToggle = async (questionId, optionIndex) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    const questionData = data.find(q => q.id === questionId);
    const selectedText = questionData.options[optionIndex].text;
    const isCorrect = String(selectedText) === String(questionData.correctAnswer);
    
    // 🔹 New: Get existing history for this question
    const existingQuestion = savedProgress?.find(q => String(q.questionId) === String(questionId));
    const newHistory = existingQuestion?.attemptHistory ? [...existingQuestion.attemptHistory] : [];
    
    // Only record if it's a new unique click (don't spam the same choice)
    if (newHistory[newHistory.length - 1] !== selectedText) {
      newHistory.push(selectedText);
    }

    const newResponse = {
      questionId: questionId,
      userAnswer: [selectedText], // This remains the "current" answer
      isCorrect: isCorrect,
      attemptHistory: newHistory, // 🔹 Now the teacher sees every attempt!
      firstChoice: newHistory[0]   // 🔹 This captures their initial instinct
    };

    // 3. Merge with ALL historical questions to preserve progress
    const previousQuestions = savedProgress || [];
    const mergedQuestions = [...previousQuestions];
    const existingIndex = mergedQuestions.findIndex(q => String(q.questionId) === String(questionId));

    if (existingIndex !== -1) {
      mergedQuestions[existingIndex] = newResponse;
    } else {
      mergedQuestions.push(newResponse);
    }

    // 4. SAVE TO FIREBASE (No Submit Button Needed)
    try {
      const lid = String(lessonId).startsWith('lesson') ? lessonId : `lesson${lessonId}`;
      const progressData = {
        readingPassage: {
          questions: mergedQuestions,
          passageCompleted: true
        }
      };

      await saveLessonProgress(userId, lid, progressData);
      
      // 5. Tell the parent to refresh so feedback colors update
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      console.error("Failed to save progress:", err);
    }
  };

  return (
    <div style={styles.quizContainer}>
      {data.map(q => {
        const userIndices = answers[q.id] || [];
        const hasAnswered = userIndices.length > 0;
        
        // Find this specific question in the "Source of Truth" (savedProgress)
        const historyData = savedProgress?.find(p => String(p.questionId) === String(q.id));
        const isCorrect = historyData?.isCorrect;

        return (
          <div key={q.id} style={styles.questionBlock}>
            <p style={styles.questionText}>{q.question}</p>
            
            {q.options.map((option, index) => {
              const isSelected = userIndices.includes(index);
              
              let optionStyle = { ...styles.optionBox };
              if (isSelected) {
                // If it's selected and we have history, show Green or Red
                if (historyData) {
                  optionStyle.backgroundColor = isCorrect ? '#d1fae5' : '#fee2e2';
                  optionStyle.color = isCorrect ? '#065f46' : '#991b1b';
                  optionStyle.borderColor = isCorrect ? '#10b981' : '#ef4444';
                  optionStyle.borderStyle = 'solid';
                  optionStyle.borderWidth = '1px';
                } else {
                  // While waiting for save, just show blue
                  optionStyle = { ...optionStyle, ...styles.optionBoxSelected };
                }
              }

              return (
                <div key={index} style={optionStyle} onClick={() => handleOptionToggle(q.id, index)}>
                  <span style={styles.optionLabel}>{String.fromCharCode(65 + index)}</span>
                  <span>{option.text}</span>
                </div>
              );
            })}
            
            {/* 🔹 FEEDBACK ZONE: Shows immediately after history is confirmed */}
            {hasAnswered && historyData && (
              <div style={{
                ...styles.feedbackContainer,
                backgroundColor: isCorrect ? '#f0fdf4' : '#fef2f2',
                borderLeft: `5px solid ${isCorrect ? '#10b981' : '#ef4444'}`
              }}>
                <p style={{ fontWeight: 'bold', margin: '0 0 5px 0', color: isCorrect ? '#15803d' : '#b91c1c' }}>
                  {isCorrect ? "Correct!" : "Incorrect"}
                </p>
                <div style={styles.feedbackText}>
                  {q.options[userIndices[0]]?.explanation}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Quiz;

const styles = {
  quizContainer: { width: '100%' },
  questionBlock: { backgroundColor: '#fff', padding: '15px', borderRadius: '12px', marginBottom: '15px' },
  questionText: { fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '15px', color: '#1e3a8a' },
  optionBox: { display: 'flex', alignItems: 'center', padding: '14px', marginBottom: '10px', borderRadius: '8px', backgroundColor: '#f3f4f6', cursor: 'pointer', transition: '0.2s' },
  optionBoxSelected: { backgroundColor: '#1e3a8a', color: '#fff' },
  optionLabel: { marginRight: '12px', fontWeight: 'bold' },
  feedbackContainer: { marginTop: '15px', padding: '15px', borderRadius: '8px' },
  feedbackText: { fontSize: '1rem', lineHeight: '1.5', color: '#374151' }
};